import {
  applyKnopkaBookingPageCustomScripts,
  clearKnopkaBookingPageCustomScripts,
} from "./knopkaCustomScripts";
import { applyGlassPerformanceClass, clearGlassPerformanceClass } from "./knopkaGlassPerformance";
import {
  applyGlassCssVars,
  clearGlassCssVars,
  defaultGlassSettings,
  parseGlassSettings,
  type KnopkaGlassSettings,
} from "./knopkaGlassTokens";

export type KnopkaBookingPageAppearance = {
  enabled: boolean;
  backgroundUrl: string;
  overlayOpacity: number;
  customCss: string;
  customScripts: string;
  glassSettings: KnopkaGlassSettings;
  active: boolean;
};

const CUSTOM_CSS_STYLE_ID = "knopka-page-custom-css";

export function emptyBookingPageAppearance(): KnopkaBookingPageAppearance {
  return {
    enabled: false,
    backgroundUrl: "",
    overlayOpacity: 55,
    customCss: "",
    customScripts: "",
    glassSettings: defaultGlassSettings(),
    active: false,
  };
}

export function parseBookingPageAppearance(data: Record<string, unknown> | null | undefined): KnopkaBookingPageAppearance {
  if (!data?.ok) return emptyBookingPageAppearance();
  const backgroundUrl = String(data.background_url || data.backgroundUrl || "").trim();
  const enabled = Boolean(data.enabled);
  const overlayOpacity = Math.max(
    0,
    Math.min(90, Number(data.overlay_opacity ?? data.overlayOpacity ?? 55) || 55)
  );
  const customCss = String(data.custom_css || data.customCss || "");
  const customScripts = String(data.custom_scripts || data.customScripts || "");
  const glassSettings = parseGlassSettings(data.glass_settings ?? data.glassSettings);
  return {
    enabled,
    backgroundUrl,
    overlayOpacity,
    customCss,
    customScripts,
    glassSettings,
    active: Boolean(data.active ?? (enabled && backgroundUrl)),
  };
}

export function applyKnopkaBookingPageAppearance(appearance: KnopkaBookingPageAppearance): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.add("knopka-booking-page");
  root.classList.toggle("knopka-custom-design", appearance.active);

  if (appearance.backgroundUrl) {
    root.style.setProperty(
      "--knopka-tg-bg-image",
      `url("${appearance.backgroundUrl.replace(/"/g, '\\"')}")`
    );
  } else {
    root.style.removeProperty("--knopka-tg-bg-image");
  }

  root.style.setProperty("--knopka-tg-bg-overlay", String(appearance.overlayOpacity / 100));

  applyGlassCssVars(root, appearance.glassSettings, appearance.active);
  applyGlassPerformanceClass(root, appearance.glassSettings.performance, appearance.active);

  const css = appearance.customCss.trim();
  let styleEl = document.getElementById(CUSTOM_CSS_STYLE_ID) as HTMLStyleElement | null;
  if (css) {
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = CUSTOM_CSS_STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = css;
  } else if (styleEl) {
    styleEl.remove();
  }

  applyKnopkaBookingPageCustomScripts(appearance.active ? appearance.customScripts : "");
}

export function clearKnopkaBookingPageAppearance(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.remove("knopka-booking-page", "knopka-custom-design");
  root.style.removeProperty("--knopka-tg-bg-image");
  root.style.removeProperty("--knopka-tg-bg-overlay");
  clearGlassCssVars(root);
  clearGlassPerformanceClass(root);
  document.getElementById(CUSTOM_CSS_STYLE_ID)?.remove();
  clearKnopkaBookingPageCustomScripts();
}

const RESERVED_SEGMENTS = new Set([
  "api",
  "auth",
  "login",
  "signup",
  "settings",
  "event-types",
  "bookings",
  "availability",
  "teams",
  "apps",
  "more",
  "payment",
  "routing",
  "workflow",
  "insights",
  "refer",
  "upgrade",
  "billing",
  "booking",
  "d",
  "embed",
]);

/** Extract booking page username from pathname (/ednaarazi, /ednaarazi/slug, /team/acme/slug). */
export function bookingPageUsernameFromPath(pathname: string | null | undefined): string | null {
  const parts = (pathname || "").split("/").filter(Boolean);
  if (!parts.length) return null;
  if (parts[0] === "team" && parts[1]) return parts[1].toLowerCase();
  if (RESERVED_SEGMENTS.has(parts[0])) return null;
  return parts[0].toLowerCase();
}
